import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function NewsletterSection() {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error("Please enter your email.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/newsletter/subscribe`, {
        email: email.trim(),
        name: name.trim() || null,
      });
      toast.success("Welcome to the inner circle.", {
        description: "You will receive Asymmetry dispatches first.",
      });
      setEmail("");
      setName("");
    } catch (err) {
      const detail =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Subscription failed. Please try again.";
      toast.error(typeof detail === "string" ? detail : "Subscription failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="newsletter"
      data-testid="newsletter-section"
      className="relative bg-midnight py-24 md:py-36 px-6 md:px-12 lg:px-24 overflow-hidden"
    >
      {/* Mega faded type */}
      <div
        aria-hidden
        className="absolute -bottom-6 left-0 right-0 text-center pointer-events-none select-none"
      >
        <span className="mega-text text-[14vw] text-white/[0.03]">
          DENIMGLOW
        </span>
      </div>

      <div className="relative grid grid-cols-1 md:grid-cols-12 gap-12 items-end">
        <div className="md:col-span-7">
          <div className="eyebrow mb-6">Dispatch · 001</div>
          <motion.h2
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.9 }}
            className="font-serif text-white text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tight"
          >
            Whispers from
            <br />
            the <span className="italic text-silver">house</span>.
          </motion.h2>
          <p className="mt-6 max-w-md text-white/60 font-light leading-relaxed">
            Private previews, atelier notes, and first access to each new
            Edit. No noise — only Asymmetry.
          </p>
        </div>

        <form
          onSubmit={handleSubmit}
          data-testid="newsletter-form"
          className="md:col-span-5 space-y-6"
        >
          <div>
            <label className="text-[10px] tracking-[0.35em] uppercase text-silver/70">
              Name (optional)
            </label>
            <input
              data-testid="newsletter-name-input"
              type="text"
              className="lux-input"
              placeholder="Your name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div>
            <label className="text-[10px] tracking-[0.35em] uppercase text-silver/70">
              Email
            </label>
            <input
              data-testid="newsletter-email-input"
              type="email"
              required
              className="lux-input"
              placeholder="you@maison.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <button
            type="submit"
            disabled={submitting}
            data-testid="newsletter-submit"
            className="group inline-flex items-center gap-3 border border-white/30 text-white px-8 py-4 uppercase tracking-[0.32em] text-[11px] hover:border-white hover:bg-white/5 transition-all duration-500 disabled:opacity-50"
          >
            {submitting ? "Subscribing…" : "Join the circle"}
            <ArrowUpRight
              size={14}
              strokeWidth={1.3}
              className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform"
            />
          </button>
          <p className="text-[10px] tracking-[0.25em] uppercase text-white/35">
            By subscribing you agree to receive private dispatches from Asymmetry Edits.
          </p>
        </form>
      </div>
    </section>
  );
}
